// Mobile Mail — Compose view (Gmail Mobile Web 风格).
// Top bar: 閉じる (left) / 送信 (right).
// Form: To / 件名 / 本文.
// Reuses PC composer/useDraftForm (state) + mailV2Api (submit).
// Cancel = silent discard (mobile has no Draft folder, saving would be unreachable).
// Validation: To + 件名 + 本文 all required before Send.

import { useMemo, useState } from "react";
import { useDraftForm, createEmptyDraftForm } from "../../modules/mail-v2/composer/useDraftForm";
import { mailV2Api } from "../../api/mail-v2";

export interface ReplyInit {
  parentMessageId: string;
  to: string;
  subject: string;
}

interface Props {
  accountId: string;
  mode: "new" | "reply";
  replyInit: ReplyInit | null;
  onCancel: () => void;
  onSent: () => void;
}

function uuidv4(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const MobileMailCompose = ({
  accountId,
  mode,
  replyInit,
  onCancel,
  onSent,
}: Props) => {
  // initial is captured once on first render; orchestrator remounts on view change.
  const initial = useMemo(() => {
    const base = createEmptyDraftForm(accountId);
    if (mode === "reply" && replyInit) {
      return {
        ...base,
        parentMessageId: replyInit.parentMessageId,
        to: replyInit.to,
        subject: replyInit.subject,
      };
    }
    return base;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { draft, setField } = useDraftForm(initial);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSend =
    !sending &&
    !!accountId &&
    !!draft.to.trim() &&
    !!draft.subject.trim() &&
    !!draft.bodyText.trim();

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const saved = await mailV2Api.upsertDraft({
        idempotency_key: uuidv4(),
        mail_account_id: accountId,
        parent_message_id: draft.parentMessageId,
        to_addresses: draft.to.trim(),
        subject: draft.subject.trim(),
        body_text: draft.bodyText.trim(),
      });
      await mailV2Api.sendDraft(saved.id, uuidv4());
      onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : "送信に失敗しました");
      setSending(false);
    }
  };

  return (
    <>
      <header className="mobile-compose-bar">
        <button
          type="button"
          className="mobile-compose-bar-button"
          onClick={onCancel}
          disabled={sending}
        >
          閉じる
        </button>
        <button
          type="button"
          className="mobile-compose-bar-button primary"
          onClick={send}
          disabled={!canSend}
        >
          {sending ? "送信中..." : "送信"}
        </button>
      </header>

      <main className="mobile-compose-body-wrap">
        <div className="mobile-compose-field">
          <span className="mobile-compose-field-label">To</span>
          <input
            className="mobile-compose-field-input"
            placeholder="宛先"
            value={draft.to}
            onChange={(e) => setField("to", e.target.value)}
            autoComplete="off"
            inputMode="email"
          />
        </div>
        <div className="mobile-compose-field">
          <span className="mobile-compose-field-label">件名</span>
          <input
            className="mobile-compose-field-input"
            placeholder="件名"
            value={draft.subject}
            onChange={(e) => setField("subject", e.target.value)}
            autoComplete="off"
          />
        </div>
        <textarea
          className="mobile-compose-body"
          placeholder="本文"
          value={draft.bodyText}
          onChange={(e) => setField("bodyText", e.target.value)}
        />
        {error && <div className="mobile-compose-error">{error}</div>}
      </main>
    </>
  );
};
